import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../contexts/LanguageContext";
import { usePostingChooser } from "../contexts/PostingChooserContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavGuard } from "../contexts/NavGuardContext";
import { getUnreadCount } from "../pages/Messages";
import PostingChooser from "./PostingChooser";
import "./BottomNav.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BottomNav() {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = dir === 'rtl';
  const { openChooser } = usePostingChooser();
  const { token } = useAuth();
  const { checkGuard } = useNavGuard();
  const [unread, setUnread] = useState(0);

  // Navigate through the guard so screens with unsaved work can intercept.
  const guardedNavigate = (path) => {
    const ok = checkGuard(path);
    if (ok === false) return;
    if (ok && typeof ok.then === 'function') {
      ok.then((allowed) => { if (allowed !== false) navigate(path); });
      return;
    }
    navigate(path);
  };

  // Poll unread count from backend
  useEffect(() => {
    if (!token) { setUnread(0); return; }
    const check = async () => {
      try {
        const r = await fetch(`${API_URL}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setUnread(d.count || 0);
        }
      } catch { setUnread(getUnreadCount()); }
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, [location.pathname, token]);

  // Detect hub context from current route
  const getHubContext = () => {
    const path = location.pathname;
    if (path.startsWith('/ladies')) return 'ladies';
    if (path.includes('student-hub/books') || path.includes('student/books')) return 'book';
    if (path.includes('student-hub/internships') || path.includes('student/internships')) return 'opportunity';
    return null;
  };

  // 4 side tabs (NOT including center sell button)
  const sideTabs = [
    { key: "home", path: "/", icon: "home", label: t('bottnav.home') },
    { key: "shop", path: "/shop", icon: "shop", label: t('bottnav.shop') },
    { key: "messages", path: "/messages", icon: "messages", label: t('bottnav.messages') },
    { key: "account", path: "/account", icon: "user", label: t('bottnav.myAccount') },
  ];

  // Split into left pair and right pair around center button
  const leftPair = sideTabs.slice(0, 2);
  const rightPair = sideTabs.slice(2, 4);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getIcon = (icon, active) => {
    const greenColor = "#1F6F3A";
    const strokeColor = active ? greenColor : "#6B7280";
    const fillColor = active ? greenColor : "none";
    switch (icon) {
      case "home":
        return (<svg width="22" height="22" fill={active ? fillColor : "none"} stroke={strokeColor} strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
      case "shop":
        return (<svg width="22" height="22" fill={active ? fillColor : "none"} stroke={strokeColor} strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 01-8 0" /></svg>);
      case "messages":
        return (<svg width="22" height="22" fill={active ? fillColor : "none"} stroke={strokeColor} strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>);
      case "user":
        return (<svg width="22" height="22" fill={active ? fillColor : "none"} stroke={strokeColor} strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
      default: return null;
    }
  };

  const renderTab = (tab) => {
    const active = isActive(tab.path);
    const badge = tab.key === 'messages' && unread > 0 ? unread : 0;
    return (
      <button key={tab.key} onClick={() => guardedNavigate(tab.path)} className={`bnav-tab ${active ? 'bnav-tab-active' : ''}`} data-testid={`nav-${tab.key}`} aria-label={tab.label}>
        <span className={`bnav-icon ${active ? 'bnav-icon-active' : ''}`}>
          {getIcon(tab.icon, active)}
          {badge > 0 && <span className="bnav-badge" data-testid="messages-unread-badge">{badge > 9 ? '9+' : badge}</span>}
        </span>
        <span className={`bnav-label ${active ? 'bnav-label-active' : ''}`}>{tab.label}</span>
      </button>
    );
  };

  return (
    <nav className="bnav" data-testid="bottom-nav">
      <div className="bnav-grid">
        {/* Slots 1-2: left pair */}
        {leftPair.map(renderTab)}
        
        {/* Slot 3: center sell button */}
        <div className="bnav-center-slot">
          <button className="bnav-sell-btn" onClick={() => openChooser(getHubContext())} data-testid="nav-add-ad" aria-label={t('bottnav.sell')}>
            <svg width="24" height="24" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        
        {/* Slots 4-5: right pair */}
        {rightPair.map(renderTab)}
      </div>

      {/* Global Posting Chooser */}
      <PostingChooser />
    </nav>
  );
}
